import api from './api';

const analyticsService = {
  // Get analytics dashboard
  // Backend accepts: ?period=30d (7d, 30d, 90d)
  getDashboard: async (startDate, endDate, connectionIds) => {
    const days = startDate && endDate
      ? Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
      : 30;
    const period = `${days}d`;
    const params = { period };
    if (connectionIds?.length) params.connectionIds = connectionIds.join(',');
    const response = await api.get('/api/v1/analytics/dashboard', { params });
    return response.data.data; // unwrap ApiResponse wrapper
  },

  // Pull latest metrics from platform (supports both platform string and connectionId)
  pullMetrics: async (platform) => {
    const response = await api.post(`/api/v1/analytics/pull/${platform}`);
    return response.data.data;
  },

  // Pull metrics for a specific connection by UUID
  pullMetricsByConnection: async (connectionId) => {
    const response = await api.post(`/api/v1/platforms/connections/${connectionId}/pull-metrics`);
    return response.data.data;
  },

  // Get per-video metrics
  getVideoMetrics: async (period = '30d') => {
    const response = await api.get('/api/v1/analytics/videos', { params: { period } });
    return response.data.data;
  },

  // Get metric history for a specific video
  getVideoMetricHistory: async (contentId) => {
    const response = await api.get(`/api/v1/analytics/videos/${contentId}`);
    return response.data.data;
  },

  // Per-format breakdown (Reels vs Photos vs Shorts vs Text). `platform` is optional;
  // when omitted axios drops the param and all platforms are included.
  getContentByType: async (platform, days = 30) => {
    const response = await api.get('/api/v1/analytics/content/by-type', { params: { platform, days } });
    return response.data.data;
  },

  // Get trend analysis (current vs previous period)
  getTrends: async (period = '30d') => {
    const response = await api.get('/api/v1/analytics/trends', { params: { period } });
    return response.data.data;
  },

  // Get top performing content
  getTopContent: async (limit = 10, period = '30d') => {
    const response = await api.get('/api/v1/analytics/top-content', { params: { limit, period } });
    return response.data.data;
  },

  // Get platform-specific breakdown
  getPlatformBreakdown: async (platform, period = '30d') => {
    const response = await api.get(`/api/v1/analytics/platform/${platform}`, { params: { period } });
    return response.data.data;
  },

  // Get engagement breakdown
  getEngagement: async (period = '30d') => {
    const response = await api.get('/api/v1/analytics/engagement', { params: { period } });
    return response.data.data;
  },

  // Get analytics summary
  getSummary: async (period = '30d') => {
    const response = await api.get('/api/v1/analytics/summary', { params: { period } });
    return response.data.data;
  },

  // Get real-time metrics (last 24h)
  getRealtime: async () => {
    const response = await api.get('/api/v1/analytics/realtime');
    return response.data.data;
  },

  // Compare two periods
  getComparison: async (period1, period2) => {
    const response = await api.get('/api/v1/analytics/comparison', { params: { period1, period2 } });
    return response.data.data;
  },

  // Get audience data
  getAudience: async (period = '30d') => {
    const response = await api.get('/api/v1/analytics/audience', { params: { period } });
    return response.data.data;
  },

  // Get connection-specific breakdown (multi-account)
  getConnectionBreakdown: async (connectionId, period = '30d') => {
    const response = await api.get(`/api/v1/analytics/connection/${connectionId}`, { params: { period } });
    return response.data.data;
  },

  // Export analytics as CSV
  exportCsv: async (period = '30d') => {
    const response = await api.get('/api/v1/analytics/export', {
      params: { period },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${period}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

export default analyticsService;
